import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronRight, 
  Check, 
  ArrowRight, 
  Palette, 
  Search, 
  Users, 
  Sparkles,
  MapPin,
  Camera,
  Flower2,
  UtensilsCrossed,
  Shirt,
  Music,
  Mail,
  Heart,
  CalendarDays
} from 'lucide-react';
import { Task } from '../kanban/types';
import { useNavigate } from 'react-router-dom';
import { Vendor } from '../../../types/vendor.ts';
import StoryCard from '../../connect/components/StoryCard.tsx';

// Category icon and color mapping using modern Lucide icons
const getCategoryConfig = (categoryId: string) => {
  const configMap: Record<string, {
    icon: React.ComponentType<any>;
    bgGradient: string;
    iconColor: string;
    hoverBg: string;
  }> = {
    venue: {
      icon: MapPin,
      bgGradient: 'from-purple-100 to-violet-100',
      iconColor: 'text-purple-600',
      hoverBg: 'hover:from-purple-200 hover:to-violet-200'
    },
    photo: {
      icon: Camera,
      bgGradient: 'from-emerald-100 to-green-100',
      iconColor: 'text-emerald-600',
      hoverBg: 'hover:from-emerald-200 hover:to-green-200'
    },
    flowers: {
      icon: Flower2,
      bgGradient: 'from-pink-100 to-rose-100',
      iconColor: 'text-pink-600',
      hoverBg: 'hover:from-pink-200 hover:to-rose-200'
    },
    food: {
      icon: UtensilsCrossed,
      bgGradient: 'from-orange-100 to-amber-100',
      iconColor: 'text-orange-600',
      hoverBg: 'hover:from-orange-200 hover:to-amber-200'
    },
    attire: {
      icon: Shirt,
      bgGradient: 'from-indigo-100 to-blue-100',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:from-indigo-200 hover:to-blue-200'
    },
    music: {
      icon: Music,
      bgGradient: 'from-red-100 to-crimson-100',
      iconColor: 'text-red-600',
      hoverBg: 'hover:from-red-200 hover:to-crimson-200'
    },
    invitations: {
      icon: Mail,
      bgGradient: 'from-teal-100 to-cyan-100',
      iconColor: 'text-teal-600',
      hoverBg: 'hover:from-teal-200 hover:to-cyan-200'
    },
    honeymoon: {
      icon: Heart,
      bgGradient: 'from-rose-100 to-pink-100',
      iconColor: 'text-rose-600',
      hoverBg: 'hover:from-rose-200 hover:to-pink-200'
    },
    default: {
      icon: CalendarDays,
      bgGradient: 'from-gray-100 to-slate-100',
      iconColor: 'text-gray-600',
      hoverBg: 'hover:from-gray-200 hover:to-slate-200'
    }
  };
  
  return configMap[categoryId] || configMap.default;
};

// Add keyframe animations for the button
const buttonGlowStyles = `
  @keyframes buttonGradientGlow {
    0% {
      background-position: 0% 50%;
      box-shadow: 0px 0px 8px rgba(255, 127, 142, 0.4);
    }
    50% {
      background-position: 100% 50%;
      box-shadow: 0px 0px 12px rgba(255, 214, 182, 0.5);
    }
    100% {
      background-position: 0% 50%;
      box-shadow: 0px 0px 8px rgba(231, 216, 240, 0.5);
    }
  }

  .gradient-button {
    background: #FF4D8D;
    background-size: 200% 200%;
    color: white;
    text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .gradient-button:hover {
    animation: buttonGradientGlow 4s infinite alternate ease-in-out;
  }
`;

interface CategoryChecklistProps {
  category: {
    id: string;
    title: string;
    icon: string;
    tasks: Task[];
  };
  isExpanded: boolean;
  onToggle: () => void;
  completedTasks: Task[];
  onToggleTask: (task: Task, isCompleted: boolean) => void;
  vendors?: Vendor[];
  userLocation?: string;
  isLoadingVendors?: boolean;
}

export const CategoryChecklist: React.FC<CategoryChecklistProps> = ({
  category,
  isExpanded,
  onToggle,
  completedTasks,
  onToggleTask,
  vendors = [],
  userLocation,
  isLoadingVendors = false,
}) => {
  const navigate = useNavigate();
  const completedCount = category.tasks.filter(task => completedTasks.includes(task)).length;
  const progress = category.tasks.length === 0 ? 0 : Math.round((completedCount / category.tasks.length) * 100);
  const categoryConfig = getCategoryConfig(category.id);

  // Category-specific navigation mapping
  const getCategoryActions = (categoryId: string) => {
    switch (categoryId) {
      case 'venue':
        return [
          { 
            label: 'Find Venues', 
            path: '/connect?category=venues', 
            icon: Users,
            description: 'Browse and connect with venues'
          },
          { 
            label: 'Venue Decor Ideas', 
            path: '/explore?category=decor&tag=venue', 
            icon: Search,
            description: 'Explore venue decoration inspiration'
          },
          { 
            label: 'Design Venue', 
            path: '/design/decor', 
            icon: Palette,
            description: 'Design your venue layout'
          }
        ];
      
      case 'photo':
        return [
          { 
            label: 'Find Photographers', 
            path: '/connect?category=photography', 
            icon: Users,
            description: 'Browse photography vendors'
          },
          { 
            label: 'Photo Inspiration', 
            path: '/explore?category=photography', 
            icon: Search,
            description: 'Browse photo styles and poses'
          }
        ];
      
      case 'flowers':
        return [
          { 
            label: 'Find Florists', 
            path: '/connect?category=florists', 
            icon: Users,
            description: 'Connect with floral designers'
          },
          { 
            label: 'Floral Inspiration', 
            path: '/explore?category=florals', 
            icon: Search,
            description: 'Browse floral arrangements'
          },
          { 
            label: 'Design Florals', 
            path: '/design/florals', 
            icon: Palette,
            description: 'Create your floral design'
          }
        ];
      
      case 'food':
        return [
          { 
            label: 'Find Caterers', 
            path: '/connect?category=catering', 
            icon: Users,
            description: 'Browse catering vendors'
          },
          { 
            label: 'Menu Inspiration', 
            path: '/explore?category=catering', 
            icon: Search,
            description: 'Explore menu ideas'
          }
        ];
      
      case 'attire':
        return [
          { 
            label: 'Attire Ideas', 
            path: '/explore?category=attire', 
            icon: Search,
            description: 'Browse wedding fashion'
          },
          { 
            label: 'Design Attire', 
            path: '/design/attire', 
            icon: Palette,
            description: 'Customize your wedding look'
          }
        ];
      
      case 'music':
        return [
          { 
            label: 'Find Entertainment', 
            path: '/connect?category=entertainment', 
            icon: Users,
            description: 'Browse music and entertainment'
          }
        ];
      
      case 'invitations':
        return [
          { 
            label: 'Invitation Ideas', 
            path: '/explore?category=invitations', 
            icon: Search,
            description: 'Browse invitation designs'
          },
          { 
            label: 'Design Invitations', 
            path: '/design/proposals', 
            icon: Palette,
            description: 'Create your invitations'
          }
        ];
      
      case 'honeymoon':
        return [
          { 
            label: 'Honeymoon Ideas', 
            path: '/explore?category=honeymoon', 
            icon: Search,
            description: 'Browse honeymoon inspiration'
          }
        ];
      
      default:
        return [
          { 
            label: 'Explore Ideas', 
            path: '/explore', 
            icon: Search,
            description: 'Browse inspiration'
          },
          { 
            label: 'Find Vendors', 
            path: '/connect', 
            icon: Users,
            description: 'Connect with professionals'
          }
        ];
    }
  };

  const categoryActions = getCategoryActions(category.id);

  const handleActionClick = (path: string) => {
    navigate(path);
  };

  React.useEffect(() => {
    // Add the styles to the document
    const styleSheet = document.createElement("style");
    styleSheet.innerText = buttonGlowStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/60 overflow-hidden hover:shadow-xl transition-all duration-200">
      <div className="relative">
        <button
          onClick={onToggle}
          className="w-full px-6 py-5 hover:bg-gray-50/50 transition-all duration-200"
        >
          <div className="flex items-center">
            <div className="flex items-center space-x-4 min-w-0">
              <div className={`p-3 bg-gradient-to-br ${categoryConfig.bgGradient} ${categoryConfig.hoverBg} rounded-xl transition-all duration-200`}>
                {React.createElement(categoryConfig.icon, {
                  className: `w-6 h-6 ${categoryConfig.iconColor}`
                })}
              </div>
              <div className="min-w-0 flex-1">
                <span className="font-semibold text-gray-900 text-base sm:text-lg block truncate">
                  {category.title}
                </span>
                <div className="flex items-center mt-3 space-x-3">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-2 rounded-full bg-blue-600"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                    {completedCount}/{category.tasks.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </button>

        <div className="absolute right-6 top-1/2 -translate-y-1/2 z-10">
          <motion.button 
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium rounded-xl
              shadow-lg hover:shadow-xl
              transform transition-all duration-200
              flex items-center justify-center gap-2
              flex-shrink-0 whitespace-nowrap"
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            whileTap={{ 
              scale: 0.95,
              opacity: 0.8 
            }}
            onClick={onToggle}
            aria-label={isExpanded ? "Hide details" : "View details"}
          >
            <span>{isExpanded ? "Hide" : "View"}</span>
            <ArrowRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-6 py-4 bg-gray-50/50 backdrop-blur-md border-t border-gray-200/60">
              
              {/* Category Action Buttons */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Get Started</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categoryActions.map((action, index) => {
                    const IconComponent = action.icon;
                    return (
                      <motion.button
                        key={index}
                        onClick={() => handleActionClick(action.path)}
                        className="group flex items-center gap-3 p-3 bg-white/80 hover:bg-white border border-gray-200/60 hover:border-blue-300/60 rounded-xl transition-all duration-200 hover:shadow-md"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="p-2 bg-blue-50 group-hover:bg-blue-100 rounded-lg transition-colors duration-200">
                          <IconComponent className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium text-gray-900 group-hover:text-blue-700 transition-colors duration-200">
                            {action.label}
                          </div>
                          <div className="text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-200">
                            {action.description}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-200" />
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Subtle Vendor Showcase */}
              {vendors.length > 0 && userLocation && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Recommended Near You</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      📍 {userLocation}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {vendors.map((vendor, index) => (
                      <StoryCard
                        key={vendor.id}
                        vendor={vendor}
                        index={index}
                        compact={true}
                        onSelect={() => navigate(`/connect/vendor/${vendor.id}/${vendor.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}`)}
                        onFavoriteToggle={() => {
                          // Handle favorite toggle logic here if needed
                          console.log(`Toggled favorite for ${vendor.name}`);
                        }}
                        isFavorite={false} // You can add favorite logic here
                      />
                    ))}
                  </div>
                  
                  {/* View More Button */}
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => navigate(`/connect?category=${getCategoryActions(category.id)[0]?.path.split('=')[1] || ''}`)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
                    >
                      View all {category.title.toLowerCase()} vendors →
                    </button>
                  </div>
                </div>
              )}

              {/* Loading State for Vendors */}
              {isLoadingVendors && vendors.length === 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Finding vendors near you...</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="bg-gray-100 rounded-xl p-4 animate-pulse">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tasks List */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Tasks ({completedCount}/{category.tasks.length})</h4>
                <div className="space-y-2">
                  {category.tasks.map((task, index) => {
                    const isCompleted = completedTasks.includes(task);
                    
                    return (
                      <motion.button 
                        key={index}
                        onClick={() => onToggleTask(task, !isCompleted)}
                        className="w-full flex items-center space-x-4 p-4 rounded-xl
                          hover:bg-white/80 transition-all duration-200
                          border border-gray-200/40 hover:border-gray-300/60 hover:shadow-sm"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center cursor-pointer
                          ${isCompleted 
                            ? 'border-green-500 bg-green-500' 
                            : 'border-gray-300 hover:border-blue-500 bg-white'}
                          transition-all duration-200 shadow-sm
                        `}>
                          {isCompleted && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`text-sm text-left font-medium ${
                          isCompleted 
                            ? 'text-gray-500 line-through' 
                            : 'text-gray-900 hover:text-blue-700'
                        }`}>
                          {task.name}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};